/**
 * The happy places -- the whole game, as a table.
 *
 * A cat wants things.  Each want regrows on its own (WANT_REGROW in
 * constants.js) and is emptied by going to the place that satisfies it and
 * doing the thing there.  A cat's happiness is one minus the average want, so
 * a cat that has been everywhere recently purrs and a neglected one does not.
 *
 * The mechanic lives in one column of this table: `solo`.  A `solo` place is
 * one the brain in server/src/ai/ can walk to on its own -- flat ground, no
 * jump required, on the navigation graph.  Everything else needs hands: a leap
 * onto a car bonnet, a scramble up a shed roof, a pounce at a butterfly.  So a
 * cat left to itself keeps its own floor of happiness and cannot get past it,
 * and the difference between that floor and a purr is exactly the part a
 * person does.  Count the rows: six solo of thirteen, so an unattended cat
 * sits at 6/13 = 0.46 against a PURR_AT of 0.72.  Those three numbers are the
 * design, and changing any of them without the other two changes the game.
 * (The hunt row moved the ceiling from 6/11 = 0.55 when it landed, and the
 * seek row moved it again from 6/12 = 0.50: adding a needs-you place widens
 * the part a person does, which is what a mechanic built on the player's own
 * pounce -- or the player's own curiosity -- ought to do.)
 *
 * Coordinates are the level's own, in metres, and every one of them is a real
 * marker from game/src/level/ rather than a number typed in here -- see the
 * `mark` comment on each.  server/src/ai/nav.js checks at boot that every
 * `solo` place is actually standable and reachable from the spawn, and refuses
 * to start if one is not: a happy place the brain cannot get to is a want that
 * never empties, and the visible symptom is a cat that mopes for no reason.
 */
/**
 * How a visit is completed.
 *
 *   dwell   be inside the zone, on the ground, for DWELL seconds.  The default,
 *           and what "lie in the sun" or "nap on the deck" means.
 *   pounce  land POUNCE_HITS charged pounces inside the zone.  Standing among
 *           the butterflies is not playing with them.
 *   walk    cover WALK_METRES along the zone without touching the ground
 *           outside it.  This is the fence: arriving is easy, staying up is not.
 *   hunt    catch a live prey with a charged pounce and carry it into the
 *           house.  The one HOW that is not a zone visit at all: the prey
 *           moves, the delivery happens rooms away from where the catch did,
 *           and the server's prey simulation (server/src/prey.js) is the
 *           authority for both moments.  happiness.js's placeAt deliberately
 *           skips HUNT rows -- standing where the mice live is not hunting
 *           them -- so a HUNT row's zone is where the prey spawns, never a
 *           disc that banks anything by itself.
 *   seek    nose out one of the hidden treasures (the FINDS table at the
 *           bottom of this file).  The other HOW that is not a zone visit:
 *           the treasures are scattered across the whole street, so no one
 *           disc could hold them, and the find is judged by the server's
 *           finds simulation (server/src/finds.js) exactly as a capture is
 *           judged by the prey's.  placeAt skips SEEK rows for the HUNT
 *           row's reason -- the row's zone is a label, not a bank.
 */
export const HOW = { DWELL: "dwell", POUNCE: "pounce", WALK: "walk", HUNT: "hunt", SEEK: "seek" };
/** Charged pounces needed inside a POUNCE zone. */
export const POUNCE_HITS = 3;
/**
 * The hunt's split: a CAPTURE banks this fraction of the hunt want, and the
 * DELIVERY -- carrying the catch into the house -- banks everything left.
 *
 * A third, and the number IS the headline of the mechanic.  Emptying w of a
 * want is worth w / PLACES.length of happiness, so at the moment of a capture
 * the catch pays w/3 and the remaining 2w/3 rides home in the cat's mouth:
 * the gift on the doormat is worth exactly DOUBLE the catch in the garden.
 * (The regrow between garden and doormat adds a hair on top of the double,
 * which errs in the player's favour and is not worth an equation to remove.)
 *
 * A half was tried on paper first and rejected: it makes the trip home worth
 * merely EQUAL to the capture, and "bring me the mouse" has to beat "eat the
 * mouse where it fell" by enough that a player who has done the trip once
 * never skips it again.  Anything under a third starts making the capture
 * itself feel like a consolation prize, and the capture is the skill moment.
 */
export const HUNT_CAPTURE = 1 / 3;
/** Metres of travel needed along a WALK zone, without falling off. */
export const WALK_METRES = 5;
export const PLACES = [
    {
        "id": "sunspot",
        "label": "Sunbathe by the porch",
        "hint": "Sit on the sunny front walk below the porch steps.",
        "solo": true,
        "how": "dwell",
        "x": 0.15,
        "y": 0.125,
        "z": 4.5,
        "r": 1.4,
        "yTol": 0.5
    },
    {
        "id": "creek",
        "label": "Paddle in the creek",
        "hint": "Through the rear garden gate, across the gravel path, to the stepping stones.",
        "solo": true,
        "how": "dwell",
        "x": 2,
        "y": -0.7,
        "z": -44.209920614176696,
        "r": 1.5,
        "yTol": 0.4
    },
    {
        "id": "lawn",
        "label": "Roll on the back lawn",
        "hint": "The open grass beside the slide and swings.",
        "solo": true,
        "how": "dwell",
        "x": -2,
        "y": 0.005,
        "z": -23,
        "r": 1.7,
        "yTol": 0.5
    },
    {
        "id": "deck",
        "label": "Nap on the back patio",
        "hint": "The open stamped-concrete patio beside the turning deck stairs.",
        "solo": true,
        "how": "dwell",
        "x": -1.4,
        "y": 0.11,
        "z": -16.1,
        "r": 0.8,
        "yTol": 0.4
    },
    {
        "id": "shed",
        "label": "Nose around the shed",
        "hint": "Investigate the white flower boxes beside the blue shed.",
        "solo": true,
        "how": "dwell",
        "x": 1.45,
        "y": 0.03,
        "z": -30.2,
        "r": 1,
        "yTol": 0.5
    },
    {
        "id": "binlane",
        "label": "Patrol the bin lane",
        "hint": "Along the east side of the house, past the green bins.",
        "solo": true,
        "how": "dwell",
        "x": 6.25,
        "y": 0.005,
        "z": -9.0,
        "r": 0.55,
        "yTol": 0.5
    },
    {
        "id": "carbonnet",
        "label": "Sit on the warm car bonnet",
        "hint": "Jump onto the car on the central driveway.",
        "solo": false,
        "how": "dwell",
        "x": 3.35,
        "y": 1.002,
        "z": 3.1,
        "r": 0.8,
        "yTol": 0.2
    },
    {
        "id": "fencewalk",
        "label": "Walk the rear fence",
        "hint": "Hop onto the black rear fence and balance five metres along its rail.",
        "solo": false,
        "how": "walk",
        "x": -5,
        "y": 1.03,
        "z": -33.5,
        "r": 0.38,
        "yTol": 0.19,
        "to": {
            "x": 1.5,
            "z": -33.5
        }
    },
    {
        "id": "deckrail",
        "label": "Balance on the deck rail",
        "hint": "Up the turning stairs, then jump onto the wooden rail beside the lawn.",
        "solo": false,
        "how": "dwell",
        "x": 2.90,
        "y": 2.1775,
        "z": -18.25,
        "r": 0.45,
        "yTol": 0.13
    },
    {
        "id": "shedroof",
        "label": "Survey from the shed roof",
        "hint": "White planter, rain barrel, then claws on the shed siding to reach the roof.",
        "solo": false,
        "how": "dwell",
        "x": -0.9,
        "y": 3.025,
        "z": -31.45,
        "r": 1.1,
        "yTol": 0.3
    },
    {
        "id": "butterflies",
        "label": "Play with the butterflies",
        "hint": "Pounce three times among the butterflies in the clearing beyond the garden gate.",
        "solo": false,
        "how": "pounce",
        "x": -0.3,
        "y": -0.065,
        "z": -35.0,
        "r": 1.6,
        "yTol": 1.6
    },
    {
        "id": "hunt",
        "label": "Bring home a catch",
        "hint": "Stalk mice near the shed or birds on the front lawn. Carry your catch to the front porch doormat.",
        "solo": false,
        "how": "hunt",
        "x": 2.6,
        "y": 0.005,
        "z": -26.9,
        "r": 1.2,
        "yTol": 1
    },
    {
        "id": "seek",
        "label": "Sniff out a treasure",
        "hint": "Seven treasures are hidden by the porch, bins, flower boxes, and creek.",
        "solo": false,
        "how": "seek",
        "x": 0.3,
        "y": 0.125,
        "z": 4.0,
        "r": 0.6,
        "yTol": 0.5
    }
];
/** id -> place, and id -> index, for the wire (a need vector is in PLACES order). */
export const PLACE_BY_ID = Object.fromEntries(PLACES.map((p) => [p.id, p]));
export const PLACE_INDEX = Object.fromEntries(PLACES.map((p, i) => [p.id, i]));
/** The ones the brain will go to unaided, and the ones it will not. */
export const SOLO_PLACES = PLACES.filter((p) => p.solo);
export const HELP_PLACES = PLACES.filter((p) => !p.solo);
/**
 * The happiness an unattended cat converges on: every solo want emptied, every
 * other want full.  Exported rather than written down, so the comment at the
 * top of this file cannot go stale when a row is added.
 */
export const SOLO_CEILING = SOLO_PLACES.length / PLACES.length;
/**
 * Is (x, y, z) inside a place?
 *
 * Plan distance to the zone -- a point for a disc, a segment for a `to` run --
 * against `r`, and height against `yTol`.  The height test is not optional
 * anywhere: without it the deck rail zone contains the deck, the shed roof
 * zone contains the flowerbed under it, and both would be banked by a cat that
 * never left the ground.
 */
export function inPlace(place, x, y, z) {
    if (Math.abs(y - place.y) > place.yTol)
        return false;
    return planDistance(place, x, z) <= place.r;
}
/** Distance from (x, z) to the zone's centre line, in plan. */
export function planDistance(place, x, z) {
    if (!place.to)
        return Math.hypot(x - place.x, z - place.z);
    // Point-to-segment: project onto the run and clamp to its ends.
    const ax = place.x, az = place.z;
    const bx = place.to.x, bz = place.to.z;
    const dx = bx - ax, dz = bz - az;
    const len2 = dx * dx + dz * dz;
    const t = len2 > 0
        ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / len2))
        : 0;
    return Math.hypot(x - (ax + dx * t), z - (az + dz * t));
}
/** A point to walk to for this place: the centre, or the middle of a run. */
export function approachPoint(place) {
    if (!place.to)
        return { x: place.x, y: place.y, z: place.z };
    return { x: (place.x + place.to.x) / 2, y: place.y, z: (place.z + place.to.z) / 2 };
}
/**
 * Happiness from a want vector: one minus the mean want.
 *
 * A plain mean, not a weighted one.  A cat with one want at full and the rest
 * empty should read as *nearly* happy, and any weighting that made the hard
 * places count for more would also make the easy ones count for less -- which
 * would let a brain-only cat sit above the solo ceiling and quietly remove the
 * reason to play.
 */
export function happinessOf(wants) {
    if (!wants.length)
        return 1;
    let sum = 0;
    for (const w of wants)
        sum += w;
    return 1 - sum / wants.length;
}
/** A fresh cat's wants: everything wanted, nothing done.  A new cat is
    therefore at zero happiness and has somewhere to go, which is the right
    first impression for a game about going places. */
export function freshWants() {
    return PLACES.map(() => 1);
}
export const FINDS = [
    {
        "id": "hairtie",
        "short": "hair tie",
        "label": "the lost hair tie",
        "hint": "on the front walk",
        "x": 0.3,
        "y": 0.125,
        "z": 4
    },
    {
        "id": "coin",
        "short": "coin",
        "label": "a shiny coin",
        "hint": "by the sidewalk west of the driveway",
        "x": -6.7,
        "y": 0.07,
        "z": 8.5
    },
    {
        "id": "feather",
        "short": "feather",
        "label": "a pale feather",
        "hint": "on the neighbouring front lawn",
        "x": 12,
        "y": 0.082,
        "z": 5
    },
    {
        "id": "bottlecap",
        "short": "bottle cap",
        "label": "a bottle cap",
        "hint": "past the green bins",
        "x": 6.25,
        "y": 0.005,
        "z": -8.4
    },
    {
        "id": "jinglyball",
        "short": "jingly ball",
        "label": "the jingly ball",
        "hint": "on the deck near the turning stairs",
        "x": 2.85,
        "y": 1.18,
        "z": -17.65
    },
    {
        "id": "catmouse",
        "short": "catnip mouse",
        "label": "the catnip mouse",
        "hint": "beside the shed flower box",
        "x": 1.55,
        "y": 0.03,
        "z": -32.15
    },
    {
        "id": "snailshell",
        "short": "snail shell",
        "label": "a spiral snail shell",
        "hint": "on the creek bank east of the stepping stones",
        "x": 4.6,
        "y": -0.29,
        "z": -43.13970236179944
    }
];
/** id -> find, and id -> index: the finds state message and the record's
    collection both travel keyed to this order. */
export const FIND_BY_ID = Object.fromEntries(FINDS.map((f) => [f.id, f]));
export const FIND_INDEX = Object.fromEntries(FINDS.map((f, i) => [f.id, i]));
